import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Search,
  FileText,
  Clock,
  Shield,
  MessageSquare,
  Mail,
  Phone,
  HelpCircle,
  ChevronRight,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';

const HELP_CATEGORIES = [
  { id: 'Documents', icon: FileText, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  { id: 'Process', icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-500/10' },
  { id: 'Security', icon: Shield, color: 'text-green-500', bgColor: 'bg-green-500/10' },
];

export function CRMHelpPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch FAQs
  const { data: faqsData, isLoading } = useQuery({
    queryKey: ['faqs', selectedCategory, searchQuery],
    queryFn: () => intakeService.listFaqs({
      category: selectedCategory || undefined,
      search: searchQuery || undefined,
      isActive: true,
    }),
  });

  const faqs = faqsData?.faqs || [];

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(faq);
    return acc;
  }, {} as Record<string, typeof faqs>);

  const getCategoryInfo = (categoryId: string) => {
    return HELP_CATEGORIES.find(c => c.id === categoryId) || {
      id: categoryId,
      icon: HelpCircle,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold">Help & FAQs</h1>
        <p className="text-muted-foreground">
          Find answers to common questions about the intake process.
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <div className="grid gap-4 md:grid-cols-3">
        {HELP_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isSelected = selectedCategory === category.id;
          const count = faqs.filter(f => f.category === category.id).length;

          return (
            <Card
              key={category.id}
              className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? 'border-primary ring-1 ring-primary' : ''}`}
              onClick={() => setSelectedCategory(isSelected ? null : category.id)}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${category.bgColor}`}>
                      <Icon className={`h-5 w-5 ${category.color}`} />
                    </div>
                    <div>
                      <p className="font-medium">{category.id}</p>
                      <p className="text-sm text-muted-foreground">{count} articles</p>
                    </div>
                  </div>
                  <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* FAQs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Frequently Asked Questions</CardTitle>
          <CardDescription>
            {selectedCategory
              ? `Showing ${faqs.length} questions in ${selectedCategory}`
              : `Showing all ${faqs.length} questions`
            }
            {selectedCategory && (
              <Button
                variant="link"
                className="ml-2 h-auto p-0 text-sm"
                onClick={() => setSelectedCategory(null)}
              >
                Clear filter
              </Button>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No results found</h3>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search or filter criteria
              </p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => {
                const categoryInfo = getCategoryInfo(category);
                const CategoryIcon = categoryInfo.icon;

                return (
                  <div key={category} className="mb-6 last:mb-0">
                    {!selectedCategory && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`flex h-6 w-6 items-center justify-center rounded ${categoryInfo.bgColor}`}>
                          <CategoryIcon className={`h-3.5 w-3.5 ${categoryInfo.color}`} />
                        </div>
                        <h3 className="font-medium text-sm">{category}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {categoryFaqs.length}
                        </Badge>
                      </div>
                    )}

                    {categoryFaqs.map((faq) => (
                      <AccordionItem key={faq.id} value={faq.id} className="border-b last:border-0">
                        <AccordionTrigger className="text-left hover:no-underline">
                          <span className="text-sm font-medium">{faq.question}</span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {faq.answer}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </div>
                );
              })}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Still need help?</h3>
                <p className="text-sm text-muted-foreground">
                  Chat with our AI assistant or contact our support team directly.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/crm/chat')}>
                <MessageSquare className="mr-2 h-4 w-4" />
                Chat with AI
              </Button>
              <Button variant="outline" asChild>
                <a href="mailto:support@data-corner.com.au">
                  <Mail className="mr-2 h-4 w-4" />
                  Email Support
                </a>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                <Mail className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="font-medium">Email Support</p>
                <p className="text-sm text-muted-foreground mb-2">Get a response within 24 hours</p>
                <a
                  href="mailto:support@data-corner.com.au"
                  className="text-sm text-primary hover:underline"
                >
                  support@data-corner.com.au
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                <Phone className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="font-medium">Phone Support</p>
                <p className="text-sm text-muted-foreground mb-2">Mon-Fri, 9am-5pm AEST</p>
                <a
                  href="tel:+61299998888"
                  className="text-sm text-primary hover:underline"
                >
                  +61 2 9999 8888
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
